import {
  Pattern,
  SOLID_PATTERN_DATA,
  SOLID_PATTERN_ID,
  isSolidPatternData,
} from '@av-platform/rendering-core';

// ============================================
// Domain entities
// ============================================

export type SolutionImage = {
  id: string;
  title?: string;
  url?: string;
  image?: string;
  file?: string;
};

export type PropertyDefinition = {
  id: string;
  title?: string;
  value?: string | null;
};

export type TechnicalReportItem = {
  id: string;
  title?: string;
};

export type ProfessionBond = {
  id: string;
  title?: string;
};

export type ReferenceProduct = {
  id: string;
  id_version?: string;
  title?: string;
  status?: string;
};

export type SolutionGalleryItem = {
  id: string;
  id_detail: string;
  image_url: string | null;
  is_active: boolean;
  status: string;
  title: string;
  material_pattern?: MaterialPatternResponse | null;
};

// ============================================
// API types
// ============================================

export type SolutionPatternResponse = {
  id: string;
  is_public: boolean;
  title: string;
  file: string;
  is_active: boolean;
};

export type LoadedSolutionPattern = {
  id: string;
  title: string;
  pattern: Pattern;
  fileContent: string;
  isPublic: boolean;
};

export type UploadPatternParams = {
  file: File;
  title: string;
};

export type PatternMiniRequest = {
  title: string;
};

export type MaterialPatternRequest = {
  fg_pattern: string | null;
  bg_pattern: string | null;
  fg_color_hex: string;
  bg_color_hex: string;
  fg_pattern_detail: PatternMiniRequest;
  bg_pattern_detail: PatternMiniRequest;
  fg_scale: number | null;
  bg_scale: number | null;
};

export type UpdateMaterialPatternParams = {
  solutionId: string | null;
  data: MaterialPatternRequest;
};

export type MaterialPatternResponse = {
  id: string;
  fg_pattern: {
    id: string;
    title: string;
    file: string;
  } | null;
  bg_pattern: {
    id: string;
    title: string;
    file: string;
  } | null;
  fg_color_hex: string;
  bg_color_hex: string;
  fg_scale: number | null;
  bg_scale: number | null;
};

// ============================================
// Pattern gallery types
// ============================================

export type UploadedPattern = {
  id: string;
  name: string;
  pattern: Pattern;
  fileContent: string;
};

export { SOLID_PATTERN_ID, SOLID_PATTERN_DATA, isSolidPatternData };

export const SOLID_PATTERN: UploadedPattern = {
  id: SOLID_PATTERN_ID,
  name: 'Solid',
  pattern: SOLID_PATTERN_DATA,
  fileContent: '',
};

export function isSolidPattern(pattern: UploadedPattern | null): boolean {
  return pattern?.id === SOLID_PATTERN_ID;
}

export type PatternSettings = {
  pattern: UploadedPattern | null;
  color: string;
  scale: number;
};

export type MaterialParameters = {
  foreground: PatternSettings;
  background: PatternSettings;
};

export type PatternPreviewData = {
  fgColor: string;
  bgColor: string;
  fgScale: number | null;
  bgScale: number | null;
  fgPattern?: any;
  bgPattern?: any;
  materialPattern?: any;
};

// ============================================
// Form types
// ============================================

export type SolutionFormProps = {
  id: string;
  title?: string;
  function?: string;
  form?: string;
  execution?: string;
  images?: SolutionImage[];
  property_definition_set?: PropertyDefinition[];
  technical_report_item_set?: TechnicalReportItem[];
  profession_bond_set?: ProfessionBond[];
  reference_product_set?: ReferenceProduct[];
  material_pattern?: MaterialPatternResponse;
};

export type SolutionFormValues = {
  title: string | null;
  function: string | null;
  form: string | null;
  execution: string | null;
  images: SolutionImage[];
  property_definition_set: PropertyDefinition[];
  technical_report_item_set: TechnicalReportItem[];
  profession_bond_set: ProfessionBond[];
  reference_product_set: ReferenceProduct[];
};

// ============================================
// Component props
// ============================================

export type SolutionModalProps = {
  visible: boolean;
  title: string;
  formProps?: SolutionFormProps | null;
  onSubmit: (id: string | undefined, values: any) => void;
  onClose: () => void;
  classification?: any[];
};

export type SolutionDetailViewProps = {
  data: any;
  onEdit?: () => void;
  editDisable?: boolean;
};

export type SolutionPropertyDefinitionModalProps = {
  visible: boolean;
  formProps?: any;
  onSubmit: (values: any) => void;
  propertyId: string | null;
  onClose: () => void;
};

export type PatternSearchDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (pattern: UploadedPattern) => void;
  existingPatterns?: UploadedPattern[];
};

export type MaterialParametersDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (params: MaterialParameters) => void;
  initialValues?: MaterialParameters;
  solutionId: string | null;
  materialPatterns: MaterialPatternResponse | null;
};

export type SolutionPickerModalProps = {
  visible: boolean;
  classificationId: string | null;
  onSubmit: (values: SolutionGalleryItem) => void;
  onClose: () => void;
  isMaterial?: boolean;
  kind?: SolutionKind;
  title?: string;
};

export type SolutionKind = 'solution' | 'pattern' | 'finish';
